import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Users, Link2, Unlink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const OC = `${process.env.REACT_APP_BACKEND_URL}/api/settings/integrations/openclaw`;
const cfg = { withCredentials: true };

const IdentityRow = ({ user, onSaved }) => {
  const [tgId, setTgId] = useState(user.telegram_user_id || "");
  const [username, setUsername] = useState(user.telegram_username || "");
  const [busy, setBusy] = useState(false);
  const linked = !!user.telegram_user_id;

  const save = async (clear = false) => {
    setBusy(true);
    try {
      await axios.put(`${OC}/identities/${user.user_id}`, clear ? { telegram_user_id: null } : { telegram_user_id: tgId, telegram_username: username }, cfg);
      toast.success(clear ? `${user.name} unlinked` : `${user.name} linked to Telegram ${tgId}`);
      if (clear) { setTgId(""); setUsername(""); }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid items-center gap-2 border-b border-slate-100 py-3 md:grid-cols-[1.4fr_1fr_1fr_auto]" data-testid={`identity-row-${user.user_id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-800">{user.name} {!user.is_active && <span className="text-xs text-slate-400">(inactive)</span>}</p>
        <p className="flex items-center gap-2 text-xs text-slate-500"><span className="capitalize">{user.role}</span>
          <Badge variant="outline" className={linked ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-400"} data-testid={`identity-status-${user.user_id}`}>{linked ? "Linked" : "Not linked"}</Badge>
        </p>
      </div>
      <Input placeholder="Telegram user id" value={tgId} onChange={(e) => setTgId(e.target.value)} data-testid={`identity-tg-id-${user.user_id}`} />
      <Input placeholder="@username (optional)" value={username} onChange={(e) => setUsername(e.target.value)} data-testid={`identity-tg-username-${user.user_id}`} />
      <div className="flex gap-1">
        <Button size="sm" onClick={() => save(false)} disabled={busy || !tgId.trim()} data-testid={`identity-save-${user.user_id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}</Button>
        {linked && <Button size="sm" variant="outline" onClick={() => save(true)} disabled={busy} data-testid={`identity-unlink-${user.user_id}`}><Unlink className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

export const OpenClawIdentities = ({ requireActor, onToggleRequire, busy }) => {
  const [users, setUsers] = useState(null);
  const load = useCallback(async () => {
    const res = await axios.get(`${OC}/identities`, cfg);
    setUsers(res.data);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Failed to load staff identities")); }, [load]);

  const linkedCount = (users || []).filter((u) => u.telegram_user_id).length;

  return (
    <Card data-testid="openclaw-identities-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4" /> 4. Staff identities (who is who in Telegram)</CardTitle>
            <CardDescription>Link each staff member's Telegram user id so bot actions run with their own permissions and are recorded under their name. Staff can get their id from <code className="rounded bg-slate-100 px-1">@userinfobot</code> on Telegram.</CardDescription>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Label htmlFor="oc-require-actor" className="text-sm text-slate-600">Block unlinked users</Label>
            <Switch id="oc-require-actor" checked={!!requireActor} disabled={busy} onCheckedChange={onToggleRequire} data-testid="openclaw-require-actor-switch" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!users ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <>
            <p className="mb-2 text-xs text-slate-500" data-testid="openclaw-identities-count">{linkedCount} of {users.length} staff linked</p>
            {users.map((u) => <IdentityRow key={u.user_id} user={u} onSaved={load} />)}
          </>
        )}
      </CardContent>
    </Card>
  );
};
